/**
 * Phase STYLE-YOURSELF-LOADING-SILHOUETTE
 *
 * Result surface for a style_yourself LB4 render. Handles BOTH:
 *   - Loading: canonical LookbookLoadingSilhouette (parameterised
 *     SilhouetteModel + rotating copy + coral progress bar), matching
 *     LB1/LB2/LB3 loading pattern across the main Results screen.
 *   - Ready:   final image + Save / Try another / Tweak this combo
 *
 * Locked decisions (Brief STYLE-YOURSELF-LOADING-SILHOUETTE):
 *   D1 — single canonical loading component (no manual animate-pulse)
 *   D2 — label "Styled by you" (mirrors LB2's "Styled for you")
 *   D3 — share icon hidden until ready
 *   D4 — "Tweak this combo" only shown after ready; back chevron always works
 *
 * Share flow reuses buildBadgedLookbookBlob + fireTracking from
 * LookbookActionButtons. Deep link pattern matches LB1/2/3:
 * https://like2fit.com?outfit={id}
 */
import React, { useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  buildBadgedLookbookBlob,
  fireTracking,
} from "@/components/LookbookActionButtons";
import {
  LookbookLoadingSilhouette,
  type LookbookModelProfile,
} from "@/components/LookbookLoadingSilhouette";
import { StyleYourselfHeader } from "./StyleYourselfHeader";

type Props = {
  outfitId: string;
  imageUrl: string | null;
  loading: boolean;
  // LB4-RECENTS-SAVED-ROUTING: optional. Only consumed by the loading
  // silhouette branch; Recents/Saved entry points never enter loading
  // (they only mount when lookbook_image_url is present) so callers
  // routing from those surfaces can omit it.
  modelProfile?: LookbookModelProfile;
  onBack: () => void;
  onTryAnother: () => void;
  onTweakCombo: () => void;
  initiallySaved?: boolean;
  hideTryAnother?: boolean;
};

const BADGE_TEXT = "Like2Fit · Styled by you";
const FILENAME = "like2fit-styled-by-you.jpg";

export function StyleYourselfResult({
  outfitId,
  imageUrl,
  loading,
  modelProfile,
  onBack,
  onTryAnother,
  onTweakCombo,
  initiallySaved,
  hideTryAnother,
}: Props) {

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(initiallySaved ?? false);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  async function handleSave() {
    if (saving || saved || !imageUrl) return;
    setSaving(true);
    setError(null);
    const { error: updateErr } = await supabase
      .from("outfits")
      .update({ is_saved: true } as never)
      .eq("id", outfitId);
    setSaving(false);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    setSaved(true);
  }

  async function handleShare() {
    if (sharing || !imageUrl) return;
    fireTracking("LB4", "share");
    setSharing(true);
    try {
      const blob = await buildBadgedLookbookBlob(imageUrl, BADGE_TEXT);
      const file = new File([blob], FILENAME, { type: "image/jpeg" });
      const nav = navigator as Navigator & {
        canShare?: (data: { files: File[] }) => boolean;
      };
      const deepLink = `https://like2fit.com?outfit=${encodeURIComponent(outfitId)}`;
      const sharePayload: ShareData = {
        files: [file],
        title: "Check out my Like2Fit look",
        text: "See this outfit styled on my body. Try yours at like2fit.com",
        url: deepLink,
      };
      if (
        typeof nav.canShare === "function" &&
        nav.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        await navigator.share(sharePayload);
      } else {
        // Fallback: trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = FILENAME;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === "AbortError") return;
      toast.error("Couldn't share — try again.");
    } finally {
      setSharing(false);
    }
  }

  // D3 — hide share icon entirely until ready
  const shareIcon = !loading && imageUrl ? (
    <button
      type="button"
      onClick={handleShare}
      disabled={sharing}
      aria-label="Share look"
      style={{
        width: 36,
        height: 36,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        border: "none",
        cursor: sharing ? "default" : "pointer",
        color: "#0A0A0A",
      }}
    >
      {sharing ? (
        <Loader2 size={18} className="animate-spin" />
      ) : (
        <Share2 size={18} />
      )}
    </button>
  ) : undefined;

  return (
    <div className="flex flex-col h-full" style={{ background: "#FFFCF8" }}>
      <StyleYourselfHeader
        eyebrow="LIKE2FIT · STYLED BY YOU"
        title={loading ? "Your look" : "Your look"}
        onBack={onBack}
        rightAction={shareIcon}
      />

      <div className="flex-1 overflow-y-auto">
        {/* 3:4 portrait — canonical loading silhouette OR rendered image */}
        <div className="px-4 pt-2">
          {loading || !imageUrl ? (
            modelProfile ? (
              <LookbookLoadingSilhouette
                modelProfile={modelProfile}
                label="Styled by you"
                messages={[
                  "Mixing your look...",
                  "Styling your combo...",
                  "Putting it on your model...",
                  "Almost ready...",
                ]}
              />
            ) : (
              <div
                className="relative w-full overflow-hidden flex items-center justify-center"
                style={{ aspectRatio: "3 / 4", background: "var(--surface-2)", borderRadius: 20 }}
              >
                <Loader2 size={28} className="animate-spin opacity-60" />
              </div>
            )
          ) : (
            <div
              className="relative w-full overflow-hidden"
              style={{ aspectRatio: "3 / 4", background: "var(--surface-2)", borderRadius: 20 }}
            >
              <img
                src={imageUrl}
                alt="Your styled look"
                className="absolute inset-0 w-full h-full"
                style={{ objectFit: "cover" }}
              />
              <div className="l2f-type-caption absolute bottom-3 left-3 px-2 py-1 rounded-md bg-black/70 text-white">
                {BADGE_TEXT}
              </div>
            </div>
          )}
        </div>

        {error ? (
          <p className="l2f-type-caption px-4 pt-2 text-destructive">{error}</p>
        ) : null}
      </div>

      {/* Sticky footer — only renders when image ready (silhouette owns full content area while loading) */}
      {!loading && imageUrl ? (
        <div
          className={
            hideTryAnother
              ? "px-4 py-3 border-t"
              : "px-4 py-3 border-t grid grid-cols-2 gap-3"
          }
          style={{ background: "#FFFCF8", borderColor: "var(--border)" }}
        >
          <Button
            onClick={handleSave}
            disabled={saving || saved}
            className={hideTryAnother ? "w-full" : undefined}
          >
            {saved ? "Saved" : saving ? "Saving…" : "Save look"}
          </Button>
          {!hideTryAnother && (
            <Button variant="outline" onClick={onTryAnother}>
              Try another
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );
}

